import { Box, ButtonBase, ClickAwayListener, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import palette from "theme/palette";
import { ScopeChip, ScopeGroup } from "./types";
import { getChipMeasureStyle, getMeasuredWidth } from "./utils";

interface ScopeChipsRowProps {
  group: ScopeGroup;
}

const GAP = 4;

const ScopeChipsRow = ({ group }: ScopeChipsRowProps) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const countRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(group.chips.length);

  const hiddenCount = group.chips.length - visibleCount;
  const visibleChips = useMemo(() => group.chips.slice(0, visibleCount), [group.chips, visibleCount]);

  useEffect(() => {
    const updateVisibleCount = () => {
      const rowWidth = rowRef.current?.clientWidth;
      const labelWidth = labelRef.current?.offsetWidth || 0;
      const countWidth = countRef.current?.offsetWidth || 0;

      if (!rowWidth) return;

      const overflowButtonMaxWidth = getMeasuredWidth(`+${group.chips.length}`, "skill");
      const availableWidth = rowWidth - labelWidth - countWidth - overflowButtonMaxWidth - 24;

      if (availableWidth <= 0) {
        setVisibleCount(0);
        return;
      }

      let usedWidth = 0;
      let nextVisibleCount = group.chips.length;

      for (let index = 0; index < group.chips.length; index += 1) {
        const chip = group.chips[index];
        const chipWidth = getMeasuredWidth(chip.title, chip.kind);
        const widthWithGap = index === 0 ? chipWidth : chipWidth + GAP;
        const remainingItemsCount = group.chips.length - index - 1;
        const shouldReserveOverflowWidth = remainingItemsCount > 0 ? overflowButtonMaxWidth + GAP : 0;

        if (usedWidth + widthWithGap + shouldReserveOverflowWidth > availableWidth) {
          nextVisibleCount = index;
          break;
        }

        usedWidth += widthWithGap;
      }

      setVisibleCount(nextVisibleCount);
    };

    updateVisibleCount();

    const resizeObserver = new ResizeObserver(updateVisibleCount);

    if (rowRef.current) resizeObserver.observe(rowRef.current);

    return () => resizeObserver.disconnect();
  }, [group.chips]);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Stack
        ref={rowRef}
        direction='row'
        spacing={1}
        alignItems='center'
        sx={{ position: "relative", minWidth: 0 }}>
        <Typography
          ref={labelRef}
          sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", flexShrink: 0 }}>
          {group.label}
        </Typography>
        <Box
          ref={countRef}
          sx={{
            px: 0.75,
            py: 0.125,
            borderRadius: 0.75,
            backgroundColor: "third.main",
            color: "primary.main",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}>
          {group.chips.length}
        </Box>
        <Stack
          direction='row'
          spacing={0.5}
          alignItems='center'
          sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          {visibleChips.map((chip) => {
            const styles = getChipMeasureStyle(chip.kind);

            return (
              <Box
                key={chip.id}
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  border: `1px solid ${styles.borderColor}`,
                  backgroundColor: styles.backgroundColor,
                  color: styles.color,
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: "18px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}>
                {chip.title}
              </Box>
            );
          })}
          {hiddenCount > 0 && (
            <ButtonBase
              onClick={() => setOpen((prev) => !prev)}
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 999,
                border: `1px solid ${palette.divider}`,
                backgroundColor: "bgSwitch",
                color: "primary.main",
                fontSize: 13,
                fontWeight: 600,
                lineHeight: "18px",
                whiteSpace: "nowrap",
                flexShrink: 0,
                "&:hover": { backgroundColor: "third.main" },
              }}>
              +{hiddenCount}
            </ButtonBase>
          )}
        </Stack>
        {open && hiddenCount > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              zIndex: 2,
              maxWidth: 420,
              p: 1,
              borderRadius: 1,
              border: `1px solid ${palette.border}`,
              backgroundColor: "white",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            }}>
            <Stack
              direction='row'
              spacing={0.5}
              useFlexGap
              flexWrap='wrap'>
              {group.chips.map((chip: ScopeChip) => {
                const styles = getChipMeasureStyle(chip.kind);

                return (
                  <Box
                    key={`dropdown-${chip.id}`}
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 999,
                      border: `1px solid ${styles.borderColor}`,
                      backgroundColor: styles.backgroundColor,
                      color: styles.color,
                      fontSize: 13,
                      fontWeight: 500,
                      lineHeight: "18px",
                      whiteSpace: "nowrap",
                    }}>
                    {chip.title}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}
      </Stack>
    </ClickAwayListener>
  );
};

export default ScopeChipsRow;
